import React, { useState, useLayoutEffect } from "react";
import { View, ActivityIndicator } from "react-native";
import { router, useGlobalSearchParams, useRootNavigationState } from "expo-router";
import { useNavigation } from "@react-navigation/native";
import { ShiftType } from "@/types/db/shifts";
import { RelatedTimecardsScreen } from "@/components/screens/shared/RelatedTimecardsScreen";
import { RelatedUsersScreen } from "@/components/screens/shared/RelatedUsersScreen";
import { RelatedExpensesScreen } from "@/components/screens/shared/RelatedExpensesScreen";
import { RelatedIncidentsScreen } from "@/components/screens/shared/RelatedIncidentsScreen";
import { SmallToggle } from "@/components/SmallToggle";
import DrawerNavigator, { Drawer } from "@/components/screens/shared/DrawerNavigator";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { useTheme } from "@/hooks/useTheme";

// Wrapper component for Time Cards screen with header toggle
function TimecardsScreenWithHeader() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const navigation = useNavigation();
  const [showApproved, setShowApproved] = useState(true);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.time_cards?.plural || "Time Cards",
      headerRight: () => (
        <View style={{ marginRight: 16 }}>
          <SmallToggle value={showApproved} onValueChange={setShowApproved} label="Show Approved" />
        </View>
      ),
    });
  }, [navigation, showApproved, colors, displayNames]);

  return <RelatedTimecardsScreen showApproved={showApproved} />;
}

// Wrapper for Incidents screen
function IncidentsScreenWithHeader() {
  const { displayNames } = useAuth();
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.incidents?.plural || "Incidents",
    });
  }, [navigation, displayNames]);

  return <RelatedIncidentsScreen />;
}

export interface ShiftRelatedTabProps {
  shift: ShiftType | null;
  loading?: boolean;
}

export function ShiftRelatedTab({ shift, loading = false }: ShiftRelatedTabProps) {
  const { loading: authLoading, displayNames } = useAuth();
  const { time_cards, users, expenses, incidents } = usePermissions();
  const { colors } = useTheme();
  const params = useGlobalSearchParams<{ related?: string }>();
  const rootNavigationState = useRootNavigationState();

  // Wait for auth to load before rendering navigator
  if (authLoading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  // Permission checks
  const canReadTimeCards = time_cards.read;
  const canReadUsers = users.read;

  // Build available screens based on permissions
  const availableScreens: string[] = [];
  if (canReadUsers) availableScreens.push("Users");
  if (canReadTimeCards) availableScreens.push("Timecards");
  if (expenses.read) availableScreens.push("Expenses");
  if (incidents.read) availableScreens.push("Incidents");

  // Validate requested route exists
  const getValidInitialRoute = (requested?: string): string | undefined => {
    if (!requested) return undefined;
    return availableScreens.find((s) => s.toLowerCase() === requested.toLowerCase());
  };

  const handleScreenChange = (screenName: string) => {
    // Only set params after Root Layout is mounted
    if (!rootNavigationState?.key) return;
    router.setParams({ related: screenName });
  };

  return (
    <DrawerNavigator
      initialRouteName={getValidInitialRoute(params.related)}
      onScreenChange={handleScreenChange}
      entityType="shift"
    >
      {canReadUsers && (
        <Drawer.Screen
          name="Users"
          component={RelatedUsersScreen}
          options={{ title: displayNames?.users?.plural || "Users" }}
        />
      )}
      {canReadTimeCards && (
        <Drawer.Screen
          name="Timecards"
          component={TimecardsScreenWithHeader}
          options={{ title: displayNames?.time_cards?.plural || "Timecards" }}
        />
      )}
      {expenses.read && (
        <Drawer.Screen
          name="Expenses"
          component={RelatedExpensesScreen}
          options={{ title: displayNames?.expenses?.plural || "Expenses" }}
        />
      )}
      {incidents.read && (
        <Drawer.Screen
          name="Incidents"
          component={IncidentsScreenWithHeader}
          options={{ title: displayNames?.incidents?.plural || "Incidents" }}
        />
      )}
    </DrawerNavigator>
  );
}
